import { ComponentType, PropertyConfigEntity } from '@/interfaces';
import isEmpty from 'lodash/isEmpty';
import uniqBy from 'lodash/uniqBy';
import { useMemo } from 'react';
import { useDetectConfigByPath } from './useDetectConfigByModuleName';

/**This hook will return a new object includes config to customize component
 * match with @argument type
 * Logic includes:
 * 1. Detect which module the user is currently in @pathname
 * 2. Retrieve the configuration of the component from dbConfig data
 * 3. Get the configuration by key to use in base component. Ref in transformConfigByKey function
 * */
export const useGetComponentConfigByModule: {
  <T extends Exclude<ComponentType, 'form'>>(
    type: T,
  ): ReturnType<typeof transformConfigByKey>;
  <T extends 'form'>(
    type: T,
    formId: string,
  ): ReturnType<typeof transformConfigByKey>;
} = <T extends ComponentType>(type: T, formId?: string) => {
  const configurationModule = useDetectConfigByPath();

  // The custom configuration data by type should ensure that the keys in the list are unique
  const componentConfig = useMemo(() => {
    const configurationByType = configurationModule[type];
    if (isEmpty(configurationByType)) return [];

    let componentConfig: PropertyConfigEntity<T>[] = configurationByType;
    if (type === 'form' && !!formId) {
      componentConfig = configurationByType?.[formId];
    }

    return uniqBy(componentConfig, 'key') ?? [];
  }, [configurationModule, formId, type]);

  return transformConfigByKey(componentConfig);
};

const transformConfigByKey = <
  T extends ComponentType,
  Entity extends PropertyConfigEntity<T>,
>(
  configComponent: Entity[],
): Record<Entity['key'], Entity> => {
  const baseInit = {} as Record<Entity['key'], Entity>;
  if (!configComponent.length) return baseInit;

  //Transform the custom config list from array to object
  return configComponent.reduce(
    (res, config) => ({ ...res, [config.key]: config }),
    baseInit,
  );
};
